import {
  AgentConfig,
  AgentConfigGetApiRes,
  AgentConfigGetArgs,
  AgentCreateApiReq,
  AgentCreateApiRes,
  AgentCreateArgs,
  AgentDeleteApiRes,
  AgentDeleteArgs,
  AgentInvokeApiReq,
  AgentInvokeApiRes,
  AgentInvokeArgs,
  AgentInvokeLowCodeArgs,
  AgentInvokeUrlOptions,
  AgentScaleApiReq,
  AgentScaleApiRes,
  AgentScaleArgs,
  AgentsGetApiRes,
  AgentUpdateApiReq,
  AgentUpdateApiRes,
  AgentUpdateArgs,
  AgentVersionUpdateApiReq,
  AgentVersionUpdateApiRes,
  AgentVersionUpdateArgs
} from '@/types/agent';
import { CallMethodEnums } from '@/types/api';
import { envVarsProvider } from '@/utils/envVars/envVars';

import { call, executeFetch } from './utils';

const _buildAgentUrl = ( urlRoute: string, options: AgentInvokeUrlOptions ): string => {
  const urlBase = envVarsProvider.get( 'AIDPSTUDIO_API_URL' );
  const baseUrl = options.full ? urlBase : '';

  const filteredOptions = Object.entries( options )
    .filter( ( [ key, value ] ) => key !== 'full' && value !== undefined )
    .map( ( [ key, value ] ) => [ key, String( value ) ] );

  const queryString = filteredOptions.length > 0
    ? `?${ new URLSearchParams( filteredOptions ).toString() }`
    : '';

  return `${ baseUrl }${ urlRoute }${ queryString }`;
};

const getAgentInvokeAidpUrl = ( options: AgentInvokeUrlOptions ): string => {
  return _buildAgentUrl( '/api/agents/invoke', options );
};

const _getAgentInvokeLowCodeUrl = ( agentId: string, version: string, options: AgentInvokeUrlOptions ): string => {
  const urlBase = envVarsProvider.get( 'AIDPSTUDIO_API_URL' );
  const baseUrl = options.full ? urlBase : '';

  const urlRoute = `/api/agents/${ encodeURIComponent( agentId ) }/versions/${ encodeURIComponent( version ) }/invoke`;

  const filteredOptions = Object.entries( options )
    .filter( ( [ key, value ] ) => key !== 'full' && value !== undefined )
    .map( ( [ key, value ] ) => [ key, String( value ) ] );

  const queryString = filteredOptions.length > 0
    ? `?${ new URLSearchParams( filteredOptions ).toString() }`
    : '';

  return `${ baseUrl }${ urlRoute }${ queryString }`;
};

export const agentCreate = async( args: AgentCreateArgs ): Promise<AgentCreateApiRes> => {
  return await call<AgentCreateApiReq, AgentCreateApiRes>( {
    body: {
      ...args,
      context: args.context ? JSON.stringify( args.context ) : undefined,
      steps: JSON.stringify( args.steps )
    },
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/agents/create'
  } );
};

export const agentDelete = async( args: AgentDeleteArgs ): Promise<AgentDeleteApiRes> => {
  const { agentId } = args;

  return await call<undefined, AgentDeleteApiRes>( {
    isProtected: true,
    method: CallMethodEnums.DELETE,
    route: `/api/agents/${ encodeURIComponent( agentId ) }`
  } );
};

export const agentInvokeAidp = async( args: AgentInvokeArgs ): Promise<AgentInvokeApiRes> => {
  const {
    clientId,
    context,
    correlationId,
    messages,
    agentId,
    requestId,
    users,
    debug
  } = args;

  const body: AgentInvokeApiReq = {
    clientId,
    context,
    correlationId,
    messages,
    agentId,
    requestId,
    users
  };

  return await call<AgentInvokeApiReq, AgentInvokeApiRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: getAgentInvokeAidpUrl( { debug } )
  } );
};

export const agentInvokeAidpStream = async( args: AgentInvokeArgs, signal?: AbortSignal ): Promise<ReadableStream<Uint8Array>> => {
  const {
    clientId,
    context,
    correlationId,
    messages,
    agentId,
    requestId,
    users,
    debug
  } = args;

  const body: AgentInvokeApiReq = {
    clientId,
    context,
    correlationId,
    messages,
    agentId,
    requestId,
    users
  };

  const response = await executeFetch( {
    route: '/api/agents/invoke/stream',
    method: CallMethodEnums.POST,
    signal,
    isProtected: true,
    body,
    params: { debug, full: true }
  } );

  if( !response.ok ) {
    throw new Error( `HTTP error! status: ${ response.status }` );
  }

  if( !response.body ) {
    throw new Error( 'No response body' );
  }

  return response.body;
};

export const agentInvokeLowCode = async( args: AgentInvokeLowCodeArgs ): Promise<AgentInvokeApiRes> => {
  const {
    agentId,
    clientId,
    context,
    contexts,
    correlationId,
    debug,
    inputs,
    messages,
    requestId,
    users,
    version
  } = args;

  const body: AgentInvokeApiReq = {
    clientId,
    context,
    contexts,
    correlationId,
    inputs,
    messages,
    agentId: 'lowcode-agent',
    requestId,
    users
  };

  return await call<AgentInvokeApiReq, AgentInvokeApiRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: _getAgentInvokeLowCodeUrl( agentId, version.toString(), { debug } )
  } );
};

export const agentUpdate = async( args: AgentUpdateArgs ): Promise<AgentUpdateApiRes> => {
  const {
    agentId
  } = args;

  return await call<AgentUpdateApiReq, AgentUpdateApiRes>( {
    body: {
      ...args
    },
    isProtected: true,
    method: CallMethodEnums.PUT,
    route: `/api/agents/${ encodeURIComponent( agentId ) }`
  } );
};

export const agentVersionUpdate = async( args: AgentVersionUpdateArgs ): Promise<AgentVersionUpdateApiRes> => {
  const {
    agentId,
    version
  } = args;

  return await call<AgentVersionUpdateApiReq, AgentVersionUpdateApiRes>( {
    body: {
      ...args,
      context: JSON.stringify( args.context ),
      steps: JSON.stringify( args.steps )
    },
    isProtected: true,
    method: CallMethodEnums.PUT,
    route: `/api/agents/${ encodeURIComponent( agentId ) }/versions/${ encodeURIComponent( version ) }`
  } );
};

export const agentTurnOff = async( args: { agentId: string } ): Promise<void> => {
  return await call<undefined, void>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/api/agents/${ encodeURIComponent( args.agentId ) }/off`
  } );
};

export const agentTurnOn = async( args: { agentId: string } ): Promise<void> => {
  return await call<undefined, void>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/api/agents/${ encodeURIComponent( args.agentId ) }/on`
  } );
};

export const agentRestart = async( args: { agentId: string } ): Promise<void> => {
  return await call<undefined, void>( {
    isProtected: true,
    method: CallMethodEnums.POST,
    route: `/api/agents/${ encodeURIComponent( args.agentId ) }/restart`
  } );
};

export const agentsGet = async(): Promise<AgentsGetApiRes> => {
  return await call<undefined, AgentsGetApiRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/agents'
  } );
};

// only for low-code agents
export const agentConfigGet = async( args: AgentConfigGetArgs ): Promise<AgentConfig> => {
  const {
    agentId,
    version
  } = args;

  const json = await call<undefined, AgentConfigGetApiRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/agents/' + agentId + '/versions/' + version
  } );

  const url = _getAgentInvokeLowCodeUrl( args.agentId, args.version.toString(), { full: true } );

  return {
    ...json,
    context: JSON.parse( json.context ),
    steps: JSON.parse( json.steps ),
    url
  };
};

export const agentScale = async( args: AgentScaleArgs ): Promise<AgentScaleApiRes> => {
  const { agentId, ...body } = args;

  return await call<AgentScaleApiReq, AgentScaleApiRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.PUT,
    route: `/api/agents/${ encodeURIComponent( agentId ) }/scale`
  } );
};

